import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Warning,
  Error as ErrorIcon,
  AccessTime,
} from '@mui/icons-material';
import { SkillAuthConfig, SkillAuthType } from '../types/skill';

// ─── Types ────────────────────────────────────────────────────────────────────

type AuthStatus = 'unconfigured' | 'needs_setup' | 'configured' | 'active' | 'failed' | 'expiring';

interface LastTestResult {
  success: boolean;
  testedAt: string;
  error?: string;
}

export interface SkillAuthStatusBadgeProps {
  authConfig: SkillAuthConfig | null;
  lastTestResult?: LastTestResult;
  onClick?: () => void;
}

// ─── Status derivation ────────────────────────────────────────────────────────

const REQUIRED_FIELDS: Record<SkillAuthType, (keyof SkillAuthConfig)[]> = {
  none:          [],
  api_key:       ['key_value'],
  oauth:         ['access_token'],
  oauth2:        ['token_url', 'client_id', 'client_secret'],
  basic:         ['username', 'password'],
  custom_header: ['header_name', 'header_value'],
  sigv4:         ['aws_access_key_id', 'aws_secret_access_key', 'aws_region', 'aws_service'],
  oci_signing:   ['tenancy_ocid', 'user_ocid', 'fingerprint', 'private_key_pem'],
  odoo_session:  ['issue_token_url', 'login', 'password'],
  // legacy type names mapped to same requirements
  oauth2_bearer: ['access_token'],
  basic_auth:    ['username', 'password'],
};

function deriveAuthStatus(
  authConfig: SkillAuthConfig | null,
  lastTestResult?: LastTestResult,
): AuthStatus {
  if (!authConfig || authConfig.type === 'none') return 'unconfigured';

  const required = REQUIRED_FIELDS[authConfig.type] ?? [];
  const hasMissing = required.some(f => !authConfig[f]);
  if (hasMissing) return 'needs_setup';

  if (lastTestResult?.success === false) return 'failed';

  if (authConfig.expires_at) {
    const hoursLeft = (authConfig.expires_at - Date.now() / 1000) / 3600;
    if (hoursLeft < 24) return 'expiring';
  }

  // Credentials are present but no test has been run yet
  if (lastTestResult === undefined) return 'configured';

  return 'active';
}

// ─── Badge config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  AuthStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error'; icon: React.ReactElement; tooltip: string }
> = {
  unconfigured: {
    label:   'No Auth',
    color:   'default',
    icon:    <RadioButtonUnchecked fontSize="small" />,
    tooltip: 'No credentials configured — skill will call the API without authentication.',
  },
  needs_setup: {
    label:   'Setup Required',
    color:   'warning',
    icon:    <Warning fontSize="small" />,
    tooltip: 'Auth type selected but required fields are missing. Click to complete setup.',
  },
  configured: {
    label:   'Configured',
    color:   'default',
    icon:    <CheckCircle fontSize="small" />,
    tooltip: 'Credentials saved but not yet tested. Click to run a connection test.',
  },
  active: {
    label:   'Active',
    color:   'success',
    icon:    <CheckCircle fontSize="small" />,
    tooltip: 'Credentials present and last connection test passed.',
  },
  failed: {
    label:   'Auth Failed',
    color:   'error',
    icon:    <ErrorIcon fontSize="small" />,
    tooltip: 'Last connection test failed. Click to review credentials.',
  },
  expiring: {
    label:   'Token Expiring',
    color:   'warning',
    icon:    <AccessTime fontSize="small" />,
    tooltip: 'OAuth token expires within 24 hours. Click to refresh.',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const SkillAuthStatusBadge: React.FC<SkillAuthStatusBadgeProps> = ({
  authConfig,
  lastTestResult,
  onClick,
}) => {
  const status = deriveAuthStatus(authConfig, lastTestResult);
  const { label, color, icon, tooltip } = STATUS_CONFIG[status];

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        onClick={onClick}
        sx={{ cursor: onClick ? 'pointer' : 'default', fontWeight: 500 }}
      />
    </Tooltip>
  );
};

export default SkillAuthStatusBadge;
